import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Layout from "../layout/Layout";
import "./Auth.css";

const Login = () => {
  const [step, setStep] = useState("email"); // email or otp
  const [email, setEmail] = useState("");
  const [otp, setOTP] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const { sendLoginOTP, verifyLoginOTP, currentUser, refreshUserRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for messages from other components
  useEffect(() => {
    if (location.state?.message) {
      setError(location.state.message);
    }

    // Check for timeout parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("timeout") === "true") {
      setError("セッションがタイムアウトしました。再度ログインしてください。");
    }
  }, [location]);

  // If user is already logged in, redirect to home
  useEffect(() => {
    if (currentUser) {
      // Force refresh user role to ensure UI is updated
      refreshUserRole();
      navigate("/");
    }
  }, [currentUser, navigate, refreshUserRole]);

  // Timer countdown for OTP expiry
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    } else if (timer === 0 && step === "otp") {
      // When timer reaches 0, enable resend
      setCanResend(true);
    }
  }, [timer, step]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!email) {
      setError("メールアドレスを入力してください");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("有効なメールアドレスを入力してください");
      return;
    }

    setLoading(true);

    const unifiedOtpSendMessage =
      "ワンタイムパスワードを送信しました。メールをご確認ください。届かない場合は、入力内容や迷惑メールフォルダをご確認ください。";

    try {
      const result = await sendLoginOTP(email);

      if (result.success) {
        setSuccessMessage(unifiedOtpSendMessage);
        setStep("otp");
        setTimer(result.expiresIn || 300); // 5 minutes default
        setCanResend(false);
        setLoading(false); // Reset loading state after successful OTP send
      }
    } catch (error) {
      setError("");
      setSuccessMessage(unifiedOtpSendMessage);
      setStep("otp");
      setTimer(300);
      setCanResend(false);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");

    const cleanedOTP = otp.replace(/\D/g, "");

    if (cleanedOTP.length !== 6) {
      setError("6桁のワンタイムパスワードを入力してください");
      return;
    }

    setLoading(true);

    try {
      const result = await verifyLoginOTP(email, cleanedOTP);

      if (result.success) {
        setSuccessMessage("ログイン成功！リダイレクト中...");

        // Navigate based on user
        setTimeout(() => {
          navigate("/");
        }, 1500);
      }
    } catch (error) {
      setError("ワンタイムパスワードの確認に失敗しました。入力内容をご確認のうえ、再度お試しください。");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const result = await sendLoginOTP(email);

      if (result.success) {
        setSuccessMessage("新しいワンタイムパスワードを送信しました");
        setTimer(result.expiresIn || 300);
        setCanResend(false);
        setOTP("");
      }
    } catch (error) {
      setError("ワンタイムパスワードの再送信に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (e) => {
    const value = e.target.value;
    // Allow only digits and limit to 6 characters
    const cleaned = value.replace(/\D/g, "").slice(0, 6);
    setOTP(cleaned);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Layout>
      <div className="login-box">
        <h2>ログイン</h2>

        {error && <div className="error-message">{error}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}

        {step === "email" ? (
          <form onSubmit={handleSendOTP}>
            <div className="form-group">
              <label htmlFor="email">メールアドレス</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="your@email.com"
              />
            </div>

            <div className="form-group">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "送信中..." : "入力完了"}
              </button>
            </div>

            <div className="form-links">
              <Link to="/register" className="register-link">
                学生登録はこちら
              </Link>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <div className="form-group">
              <label htmlFor="otp">ワンタイムパスワード</label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={handleOTPChange}
                placeholder="6桁の数字"
                maxLength="6"
                pattern="\d{6}"
                required
                disabled={loading}
                className="otp-input"
                style={{
                  fontSize: "24px",
                  textAlign: "center",
                  letterSpacing: "8px",
                  fontFamily: "monospace"
                }}
              />
            </div>

            <div className="form-group">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "検証中..." : "ログイン"}
              </button>
            </div>

            <div style={{ textAlign: "center", marginTop: "15px" }}>
              {canResend ? (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#667eea",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "14px"
                  }}
                >
                  ワンタイムパスワードを再送信
                </button>
              ) : (
                <div>
                  <span style={{ color: "#999", fontSize: "14px" }}>
                    再送信可能まで {formatTime(timer)}
                  </span>
                </div>
              )}
            </div>

            <div style={{ textAlign: "center", marginTop: "10px" }}>
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setOTP("");
                  setTimer(0);
                  setCanResend(false);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#667eea",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontSize: "14px"
                }}
              >
                メールアドレスを変更
              </button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
};

export default Login;